// React
import { FunctionComponent, useEffect, useState } from "react"

// Redux
import { connect } from "react-redux"
import { AppState } from "src/store"

// Shared constants and interfaces
import approvalStatusCountBoilerplate from "src/components/approvals/constants/ApprovalStatusCountBoilerplate"
import { I_ApprovalStatusCounts } from "src/components/approvals/interfaces"

// Global components
import PageLoaderWithIcon from "src/components/common/PageLoaderWithIcon/PageLoaderWithIcon"

// Local components
import VehicleIncidentsHeader from "./components/VehicleIncidentsHeader/VehicleIncidentsHeader"
import VehicleIncidentsTable from "./components/VehicleIncidentsTable/VehicleIncidentsTable"
import VehicleIncidentsChooseAction from "./components/VehicleIncidentsChooseAction/VehicleIncidentsChooseAction"
import VehicleIncidentsStartDiscipline from "./components/VehicleIncidentsStartDiscipline/VehicleIncidentsStartDiscipline"
import VehicleIncidentsIgnoreIncident from "./components/VehicleIncidentsIgnoreIncident/VehicleIncidentsIgnoreIncident"
import VehicleIncidentsInitReview from "./components/VehicleIncidentsInitReview/VehicleIncidentsInitReview"
import VehicleIncidentsForwardToNextReviewer from "./components/VehicleIncidentsForwardToNextReviewer.tsx/VehicleIncidentsForwardToNextReviewer"
import VehicleIncidentsSwitchViewLoader from "./components/VehicleIncidentsSwitchView/VehicleIncidentsSwitchViewLoader/VehicleIncidentsSwitchViewLoader"
import VehicleIncidentsApprovalHistory from "./components/VehicleIncidentsApprovalHistory/VehicleIncidentsApprovalHistory"
import VehicleIncidentsVideoPlayer from "./components/VehicleIncidentsVideoPlayer/VehicleIncidentsVideoPlayer"
import VehicleIncidentsFetchPortalAPIGeneratedPDF from "./components/VehicleIncidentsFetchPortalAPIGeneratedPDF/VehicleIncidentsFetchPortalAPIGeneratedPDF"
import VehicleIncidentsEditLabels from "./components/VehicleIncidentsEditLabels"
import VehicleIncidentsEditEmployee from "./components/VehicleIncidentsEditEmployee"

// Local services and types
import { I_IncidentAction, I_SafetyEvent } from "./interfaces"
import VIS from "./VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"

//enums
import { LoadState } from "./enums"

// Styles
import "./VehicleIncidents.css"

interface Props {
  userId: number
  isAdmin: boolean
}

const VehicleIncidents: FunctionComponent<Props> = (props) => {
  const { userId, isAdmin } = props

  // State hooks for managing component data and flow
  const [loader, setLoader] = useState<LoadState>(LoadState.InitialLoad)
  const [events, setEvents] = useState<I_SafetyEvent[]>([])
  const [openVideo, setOpenVideo] = useState<number>(-1)
  const [statusCounts, setStatusCounts] = useState<I_ApprovalStatusCounts[]>(
    JSON.parse(JSON.stringify(approvalStatusCountBoilerplate))
  )
  const [incidentAction, setIncidentAction] = useState<I_IncidentAction>({
    action: 0,
    id: -1,
  })
  const [isInitialized, setIsInitialized] = useState(false)

  const { action, id } = incidentAction

  useEffect(() => {
    // Async init routine to wait for approval service and VIS
    ; (async () => {
      // Assign setter functions and user ID to VIS (shared service object)
      // This makes VIS the single source of truth for stateful logic across multiple components
      Object.assign(VIS, {
        setLoader,
        setEvents,
        setOpenVideo,
        setIncidentAction,
        setStatusCounts,
      })

      VIS.assertVehicleIncidentsSettersInitialized()

      // Store user ID and admin flag in VIS for downstream logic
      VIS.loggedInEmployeeId = userId
      VIS.isAdmin = isAdmin

      // Initialize approval service if not already initialized
      if (!approvalService.initialized) {
        await approvalService.init()
      }

      // Initialize reference data, maps, and first batch of events
      await VIS.init()

      setIsInitialized(true)
    })()
  }, [userId, isAdmin])

  if (!isInitialized) {
    return (
      <PageLoaderWithIcon
        icon="car_crash"
        loaderText="Initializing Vehicle Incidents"
      />
    )
  }

  const activeLabels = VIS.filters.labels ?? []
  const visibleEvents = activeLabels.length > 0
    ? events.filter(e => {
        const eventLabels = (e.Label ?? "").split(",").filter(Boolean)
        const nameNormalized = (e.Name ?? "").toLowerCase().replace(/\s+/g, "")
        return activeLabels.some(l =>
          eventLabels.some(el => el.toLowerCase() === l.toLowerCase()) ||
          nameNormalized === l.toLowerCase()
        )
      })
    : events

  return (
    <div className="VehicleIncidents main-content col-sm">
      {/* Main view content after initial load */}
      {loader !== LoadState.InitialLoad && (
        <>
          <VehicleIncidentsHeader statusCounts={statusCounts} />
          <VehicleIncidentsTable events={visibleEvents} loader={loader} />

          {/* Render context-specific modals or actions based on incidentAction */}
          {action === 1 && id !== -1 && (
            <VehicleIncidentsChooseAction incidentAction={incidentAction} />
          )}
          {action === 2 && id !== -1 && (
            <VehicleIncidentsStartDiscipline incidentAction={incidentAction} />
          )}
          {action === 3 && id !== -1 && (
            <VehicleIncidentsInitReview incidentAction={incidentAction} />
          )}
          {action === 4 && id !== -1 && (
            <VehicleIncidentsForwardToNextReviewer incidentAction={incidentAction} />
          )}
          {action === 5 && id !== -1 && (
            <VehicleIncidentsIgnoreIncident incidentAction={incidentAction} />
          )}
          {action === 6 && id !== -1 && (
            <VehicleIncidentsApprovalHistory incidentAction={incidentAction} />
          )}
          {action === 7 && id !== -1 && (
            <VehicleIncidentsFetchPortalAPIGeneratedPDF incidentAction={incidentAction} />
          )}
          {action === 8 && id !== -1 && (
            <VehicleIncidentsEditLabels incidentAction={incidentAction} />
          )}
          {action === 9 && id !== -1 && (
            <VehicleIncidentsEditEmployee incidentAction={incidentAction} />
          )}
        </>
      )}

      {openVideo > -1 && <VehicleIncidentsVideoPlayer id={openVideo} />}

      {/* Initial loading screen */}
      {loader === LoadState.InitialLoad && (
        <PageLoaderWithIcon
          icon="car_crash"
          loaderText="Loading Vehicle Incidents"
        />
      )}

      {/* "Switching Views" loader */}
      {loader === LoadState.SwitchingViews && <VehicleIncidentsSwitchViewLoader />}

      {loader === LoadState.ApplyingFilters && (
        <PageLoaderWithIcon
          icon="tune"
          loaderText="Applying filters..."
        />
      )}

    </div>
  )
}

// Map Redux store state to props
const mapStateToProps = (state: AppState) => ({
  userId: parseInt(state.user.userId),
  isAdmin: state.user.isAdmin,
})

export default connect(mapStateToProps)(VehicleIncidents)
